<template>
  <div ref="root">
    <UiTooltip theme="material" tooltip-position="top" :label="hint">
      <div
        ref="trigger"
        @click="showDropdown"
        class="tw:relative tw:cursor-pointer tw:border tw:rounded-lg tw:flex box-size-44 tw:items-center tw:px-3 tw:py-1 tw:pr-8 tw:h-[41px]"
        :class="getFieldClass(show, disabled)"
      >
        <span class="tw:truncate tw:absolute tw:top-[3px] tw:text-xs tw:font-semibold">{{ label }}</span>
        <span class="tw:truncate tw:absolute tw:bottom-[3px] tw:text-sm tw:max-w-[120px]">{{ $t(valueLabel) }}</span>

        <ChevronRight class="tw:absolute tw:size-6" :class="show ? 'arrow-dropdown-open' : 'arrow-dropdown'" />
      </div>
    </UiTooltip>
    <Teleport to="body">
      <div
        v-if="show && !disabled"
        ref="dropdown"
        class="tw:fixed tw:min-h-[50px] tw:w-auto tw:rounded tw:bg-white dropdown-card tw:z-[9999]"
        :style="dropdownStyle"
      >
        <ul>
          <li
            v-for="(option, index) in options"
            :key="index"
            @click="onSelect({ option, name })"
            class="tw:cursor-pointer tw:hover:bg-blue-100 tw:hover:text-blue-700 tw:p-4 nowrap tw:text-sm"
            :class="getOptionClass(option.id === value.id, index)"
          >
            {{ withTranslations ? $t(option.label) : option.label }}
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>
<script setup>
import { ref, computed, nextTick, onMounted, onDeactivated } from 'vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'

const props = defineProps({
  options: Array,
  name: String,
  value: Object,
  disabled: Boolean,
  hint: String,
  valueLabel: String,
  label: String,
  withTranslations: {
    type: Boolean,
    default: false,
  },
})

const root = ref(null)
const trigger = ref(null)
const dropdown = ref(null)
const show = ref(false)
const dropdownPosition = ref({ top: 0, left: 0, minWidth: 0 })

const dropdownStyle = computed(() => ({
  top: `${dropdownPosition.value.top}px`,
  left: `${dropdownPosition.value.left}px`,
  minWidth: `${dropdownPosition.value.minWidth}px`,
}))

const updateDropdownPosition = () => {
  if (!trigger.value) return
  const rect = trigger.value.getBoundingClientRect()
  dropdownPosition.value = {
    top: rect.bottom + 5,
    left: rect.left,
    minWidth: rect.width,
  }
}

const showDropdown = () => {
  if (!props.disabled) {
    show.value = !show.value
    if (show.value) {
      nextTick(updateDropdownPosition)
    }
  }
}
const emit = defineEmits(['select'])
const onSelect = ({ option, name }) => {
  emit('select', { option, name })
  showDropdown()
}

const handleScroll = () => {
  if (show.value) {
    show.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', handleScroll, true)
})
onDeactivated(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScroll, true)
})
const handleClickOutside = evt => {
  if (
    root?.value !== null &&
    !root?.value?.contains(evt.target) &&
    !dropdown?.value?.contains(evt.target)
  ) {
    show.value = false
  }
}

const getFieldClass = (show, disabled) => {
  const classes = []
  if (disabled) {
    classes.push('tw:border-gray-300 tw:text-gray-300')
  }
  if (show) {
    classes.push('tw:border-blue-700')
  }
  if (!show && !disabled) {
    classes.push('tw:border-gray-500')
  }
  return classes.join(' ')
}

const getOptionClass = (selected, position) => {
  const classes = []
  if (position === 0) {
    classes.push('tw:rounded-t')
  } else if (props.options.length - 1 === position) {
    classes.push('tw:rounded-b')
  }
  if (selected) {
    classes.push('tw:text-blue-700 tw:bg-blue-100')
  }

  return classes.join(' ')
}
</script>
<style scoped>
.arrow-dropdown {
  top: 50%;
  transform: translateY(-45%) rotate(90deg);
  right: 6px;
  pointer-events: none;
}
.arrow-dropdown-open {
  top: 50%;
  transform: translateY(-45%) rotate(270deg);
  right: 6px;
  pointer-events: none;
}
.dropdown-card {
  box-shadow:
    0 2px 4px 0 rgba(63, 87, 189, 0.1),
    0 2px 10px 0 rgba(108, 112, 122, 0.15);
}
.nowrap {
  text-wrap: nowrap;
}
</style>
